// ============================================================
// ShiftTemplatePage — 班別模板管理
// 老闆/主管可新增 / 編輯 / 停用 班別
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

interface ShiftTemplate {
  id: string;
  name: string;
  kind?: 'WORK' | 'REST_DAY' | 'REGULAR_LEAVE';
  startTime: string;
  endTime: string;
  breakStart: string | null;
  breakEnd: string | null;
  color: string;
  isActive: boolean;
}

// 提高彩度 + 拉開色相間距，避免班別色塊在班表上糊在一起
const COLOR_PRESETS = [
  { name: '橙黃', value: '#E89A3E' },  // 早班 暖
  { name: '海青', value: '#3F87A8' },  // 全天班 冷
  { name: '苔綠', value: '#5C9166' },  // 午班 中
  { name: '霞紫', value: '#8C6DAE' },  // 週六班 深
  { name: '紅磚', value: '#C45D4A' },  // 例假日 警示
  { name: '霧灰', value: '#7E8FA3' },  // 休息日 中性
  { name: '櫻粉', value: '#D87C90' },  // 備用
  { name: '芥黃', value: '#C5A24B' },  // 備用
];

function calcWorkHours(start: string, end: string, bs?: string | null, be?: string | null): number {
  const toMin = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
  };
  let m = toMin(end) - toMin(start);
  if (bs && be) m -= (toMin(be) - toMin(bs));
  return m / 60;
}

export function ShiftTemplatePage() {
  const [list, setList] = useState<ShiftTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<ShiftTemplate> | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<{ templates: ShiftTemplate[] }>(
        `/shift-templates?includeInactive=${includeInactive}`
      );
      setList(r.templates);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [includeInactive]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleSave = async (t: Partial<ShiftTemplate>) => {
    if (!t.name?.trim() || !t.startTime || !t.endTime) {
      alert('請填名稱、開始、結束時間');
      return;
    }
    const body = {
      name: t.name.trim(),
      kind: t.kind ?? 'WORK',
      startTime: t.startTime,
      endTime: t.endTime,
      breakStart: t.breakStart || null,
      breakEnd: t.breakEnd || null,
      color: t.color || '#8B9DAF',
    };
    try {
      if (t.id) {
        await api.put(`/shift-templates/${t.id}`, body);
      } else {
        await api.post('/shift-templates', body);
      }
      setEditing(null);
      fetchList();
    } catch (err) {
      alert('儲存失敗：' + (err as Error).message);
    }
  };

  const handleDeactivate = async (t: ShiftTemplate) => {
    if (!confirm(`停用班別「${t.name}」？\n\n（既有排班的此班別會保留，但未來不能再新建）`)) return;
    try {
      const r = await api.delete<{ message: string }>(`/shift-templates/${t.id}`);
      alert(r.message);
      fetchList();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const handleRestore = async (t: ShiftTemplate) => {
    try {
      await api.post(`/shift-templates/${t.id}/restore`, {});
      fetchList();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-morandi-800">班別管理</h1>
          <p className="text-xs text-morandi-400 mt-1">編輯排班用的班別模板，每個班別有獨立顏色</p>
        </div>
        <button
          onClick={() => setEditing({ name: '', startTime: '08:00', endTime: '17:00', color: '#8B9DAF' })}
          className="px-4 py-2 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm"
        >+ 新增班別</button>
      </div>

      <label className="text-xs text-morandi-400 flex items-center gap-1 cursor-pointer">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={e => setIncludeInactive(e.target.checked)}
        />
        顯示已停用的班別
      </label>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : list.length === 0 ? (
        <div className="card-morandi p-8 text-center text-morandi-300">尚無班別，點右上「+ 新增班別」</div>
      ) : (
        <div className="grid gap-3">
          {list.map(t => {
            const hrs = calcWorkHours(t.startTime, t.endTime, t.breakStart, t.breakEnd);
            const needsBreak = !t.breakStart && (calcWorkHours(t.startTime, t.endTime) > 4);
            return (
              <div
                key={t.id}
                className={`card-morandi p-4 ${!t.isActive ? 'opacity-50' : ''}`}
                style={{ borderLeft: `4px solid ${t.color}` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {t.kind === 'REGULAR_LEAVE' && <span className="text-base">📌</span>}
                      {t.kind === 'REST_DAY' && <span className="text-base">💤</span>}
                      <span className="text-base font-semibold text-morandi-700">{t.name}</span>
                      {t.kind === 'REGULAR_LEAVE' && <span className="badge bg-rose-100 text-rose-600 text-xs">例假日</span>}
                      {t.kind === 'REST_DAY' && <span className="badge bg-morandi-200 text-morandi-600 text-xs">休息日</span>}
                      {!t.isActive && <span className="badge bg-morandi-200 text-morandi-500 text-xs">已停用</span>}
                    </div>
                    <div className="text-sm font-mono text-morandi-600 mt-1">
                      {t.startTime} – {t.endTime}
                      <span className="ml-2 text-xs text-morandi-400">（{hrs} 小時）</span>
                    </div>
                    {t.breakStart && t.breakEnd && (
                      <div className="text-xs text-morandi-400 mt-0.5">
                        休息 {t.breakStart} – {t.breakEnd}
                      </div>
                    )}
                    {needsBreak && (
                      <div className="text-xs text-amber-600 mt-1">
                        ⚠ 連續工作超過 4 小時，依勞基法 §35 應安排 30 分鐘休息
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {t.isActive ? (
                      <>
                        <button
                          onClick={() => setEditing(t)}
                          className="text-xs text-earth-500 hover:text-earth-700"
                        >編輯</button>
                        <button
                          onClick={() => handleDeactivate(t)}
                          className="text-xs text-rose-400 hover:text-rose-600"
                        >停用</button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleRestore(t)}
                        className="text-xs text-sage-500 hover:text-sage-700"
                      >復原</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <EditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function EditModal({ initial, onClose, onSave }: {
  initial: Partial<ShiftTemplate>;
  onClose: () => void;
  onSave: (t: Partial<ShiftTemplate>) => void;
}) {
  const [form, setForm] = useState<Partial<ShiftTemplate>>(initial);
  const [hasBreak, setHasBreak] = useState(!!initial.breakStart);

  const totalHrs = form.startTime && form.endTime
    ? calcWorkHours(form.startTime, form.endTime)
    : 0;
  const netHrs = form.startTime && form.endTime
    ? calcWorkHours(form.startTime, form.endTime, hasBreak ? form.breakStart : null, hasBreak ? form.breakEnd : null)
    : 0;
  const needsBreak = totalHrs > 4 && !hasBreak;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-morandi-800">{initial.id ? '編輯班別' : '新增班別'}</h2>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">名稱 *</label>
          <input
            type="text"
            value={form.name ?? ''}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="例：早班、午班、週六班"
            maxLength={20}
            className="input-field"
          />
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">類型</label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setForm({ ...form, kind: 'WORK' })}
              className={`px-3 py-2 rounded-btn text-xs transition-colors
                ${(form.kind ?? 'WORK') === 'WORK' ? 'bg-earth-400 text-white' : 'bg-morandi-100 text-morandi-500'}`}
            >🛠 工作</button>
            <button
              onClick={() => setForm({ ...form, kind: 'REGULAR_LEAVE', startTime: '00:00', endTime: '00:00', breakStart: null, breakEnd: null })}
              className={`px-3 py-2 rounded-btn text-xs transition-colors
                ${form.kind === 'REGULAR_LEAVE' ? 'bg-rose-400 text-white' : 'bg-morandi-100 text-morandi-500'}`}
            >📌 例假日</button>
            <button
              onClick={() => setForm({ ...form, kind: 'REST_DAY', startTime: '00:00', endTime: '00:00', breakStart: null, breakEnd: null })}
              className={`px-3 py-2 rounded-btn text-xs transition-colors
                ${form.kind === 'REST_DAY' ? 'bg-morandi-400 text-white' : 'bg-morandi-100 text-morandi-500'}`}
            >💤 休息日</button>
          </div>
          <div className="text-xs text-morandi-400 mt-1">
            §36 規定每 7 天需有 1 例 + 1 休
          </div>
        </div>

        {(form.kind ?? 'WORK') === 'WORK' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-morandi-500 block mb-1">上班 *</label>
              <input
                type="time"
                value={form.startTime ?? ''}
                onChange={e => setForm({ ...form, startTime: e.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="text-sm text-morandi-500 block mb-1">下班 *</label>
              <input
                type="time"
                value={form.endTime ?? ''}
                onChange={e => setForm({ ...form, endTime: e.target.value })}
                className="input-field"
              />
            </div>
          </div>
        )}

        {totalHrs > 0 && (
          <div className="text-xs text-morandi-500 bg-morandi-50 rounded-lg px-3 py-2">
            總時間 {totalHrs} 小時，扣除休息後實際工時 {netHrs} 小時
          </div>
        )}

        {(form.kind ?? 'WORK') === 'WORK' && (
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={hasBreak}
              onChange={e => {
                setHasBreak(e.target.checked);
                if (!e.target.checked) {
                  setForm({ ...form, breakStart: null, breakEnd: null });
                }
              }}
            />
            <span className="text-sm text-morandi-700">含休息時段</span>
            {needsBreak && (
              <span className="text-xs text-amber-600 ml-auto">⚠ §35 建議</span>
            )}
          </label>

          {hasBreak && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-morandi-400 block mb-1">休息開始</label>
                <input
                  type="time"
                  value={form.breakStart ?? ''}
                  onChange={e => setForm({ ...form, breakStart: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-xs text-morandi-400 block mb-1">休息結束</label>
                <input
                  type="time"
                  value={form.breakEnd ?? ''}
                  onChange={e => setForm({ ...form, breakEnd: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>
          )}
        </div>
        )}

        <div>
          <label className="text-sm text-morandi-500 block mb-2">顏色</label>
          <div className="grid grid-cols-4 gap-2">
            {COLOR_PRESETS.map(c => (
              <button
                key={c.value}
                onClick={() => setForm({ ...form, color: c.value })}
                className={`flex items-center gap-1 px-2 py-2 rounded-btn text-xs transition-all
                  ${form.color === c.value ? 'ring-2 ring-morandi-500 bg-morandi-50' : 'hover:bg-morandi-50'}`}
              >
                <span className="w-4 h-4 rounded-full" style={{ backgroundColor: c.value }} />
                <span className="text-morandi-600">{c.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn text-sm"
          >取消</button>
          <button
            onClick={() => onSave(form)}
            className="flex-1 py-2.5 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm"
          >{initial.id ? '更新' : '新增'}</button>
        </div>
      </div>
    </div>
  );
}
